import React from 'react';

import {skipToken} from '@reduxjs/toolkit/dist/query';
import {colors} from 'js-libs/sweetcommons/styles';
import {Link} from 'react-router';
import styled from 'styled-components';

import ElapsedTime from 'app/components/general/ElapsedTime';
import {useCurrentUserState} from 'app/current-user/api';
import {
  BootstrapMaxSmallMediaQuery,
  BootstrapMinLargeMediaQuery,
  BootstrapMinMediumMediaQuery,
} from 'app/general/mediaqueries';
import {useSignoffs} from 'app/signoffs/api';
import {gt} from 'app/utils/i18n';

import type {DocumentContentType} from 'app/acceptance/types';

const StyledBarDIV = styled.div`
  padding: 10px 20px;
  text-align: center;
  transition: all 0.5s;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
  border-top: 1px solid ${colors.borderGray};
  background-color: #fff9e5;
`;

type Props = {
  objectId: number;
  contentType: DocumentContentType;
  viewChangesURL: string;
};

export function SignoffRequiredInfoBar({
  objectId,
  contentType,
  viewChangesURL,
}: Props): JSX.Element | null {
  const currentUser = useCurrentUserState().data;
  const {data: mySignoffs} = useSignoffs(
    currentUser == null
      ? skipToken
      : {
          user_id: currentUser.id,
          object_id: objectId,
          content_type: contentType,
          ordering: '-created_at',
        }
  );

  // first signoff guaranteed to be unsigned if it exists because of -created_at.
  const signoff = mySignoffs?.results?.[0] ?? null;

  if (signoff == null || signoff.signed_at != null) {
    return null;
  }

  const elapsedTime = (
    <ElapsedTime
      tooltipPlacement="top"
      ContainerTag="span"
      timestamp={signoff.created_at}
    />
  );
  return (
    <StyledBarDIV>
      <div className="d-flex align-items-center justify-content-center lead d-print-none">
        <div className="text-truncate flex-grow-1">
          <i className="fa fa-fw fa-signature mr-2 small" />
          <BootstrapMinLargeMediaQuery>
            {gt('You must read and sign off that you understand this document')}
            <span className="text-nowrap d-none d-md-inline ml-2">
              ({gt('requested %s', elapsedTime)})
            </span>
          </BootstrapMinLargeMediaQuery>
          <BootstrapMinMediumMediaQuery maxWidth={991}>
            {gt('You must read and sign off that you understand this document')}
          </BootstrapMinMediumMediaQuery>
          <BootstrapMaxSmallMediaQuery>
            {gt('You must sign off this document')}
          </BootstrapMaxSmallMediaQuery>
          <Link
            className="ml-3 btn btn-sm btn-outline-secondary"
            to={viewChangesURL}
          >
            <BootstrapMaxSmallMediaQuery>
              {gt('Changes')}
            </BootstrapMaxSmallMediaQuery>
            <BootstrapMinMediumMediaQuery>
              {gt('View Changes')}
            </BootstrapMinMediumMediaQuery>
          </Link>
        </div>
      </div>
    </StyledBarDIV>
  );
}
